import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { updatePageMeta, addBreadcrumbSchema } from '../utils/seo';
import { getPricing, getDefaultPackages, getDefaultAddOns, getLandAcreageConfig, landExtraAcres } from '../utils/pricing';

import { SERVICE_DETAIL as serviceDetail, resolveServicePrice, serviceFeatures } from '../data/services';
import { canonicalFor, metaFor, breadcrumbsFor } from '../seo/routeMeta';


// À la carte add-ons — MARKETING DISPLAY LIST
// These are shown on the public Services page as a shopping menu. They intentionally
// include per-unit pricing that doesn't map 1:1 to bookable add-ons (e.g. "$3/photo",
// "$40/room"). Bookable add-ons live in server/config/pricing.js — this list is
// marketing copy and is maintained separately.
// Display-only unit suffixes ("per photo", "per room"). Everything else about an
// add-on — id, name, price, description, icon — now comes from the shared
// pricing source rather than a copy kept here. This page had drifted badly: it
// listed four add-on IDs that no longer existed (extra-photos, virtual-twilight,
// virtual-staging, floor-plan) and was missing six that did, so the marketing
// page advertised a different menu than the booking page.
const ADDON_UNIT_SUFFIX = {
  'virtual-twilight-1': ' / photo',
  'virtual-staging-1': ' / photo'
};

// Service detail page
function ServiceDetail({ type }) {
  const service = serviceDetail[type];
  const [pricing, setPricing] = useState({ packages: getDefaultPackages(), addOns: getDefaultAddOns(), landAcreage: getLandAcreageConfig() });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const live = await getPricing();
        if (cancelled) return;
        setPricing({
          packages: Array.isArray(live?.packages) && live.packages.length ? live.packages : getDefaultPackages(),
          addOns: Array.isArray(live?.addOns) && live.addOns.length ? live.addOns : getDefaultAddOns(),
          landAcreage: live?.landAcreage || getLandAcreageConfig()
        });
      } catch {
        // Bundled defaults already in state.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const displayPrice = resolveServicePrice(service, pricing);
  const features = serviceFeatures(service, pricing);

  // Every service page has its own title, description and canonical — the
  // same ones the server prints into the raw HTML (seo/routeMeta, 2026-10-17).
  useEffect(() => {
    if (service && service.meta) {
      updatePageMeta({ ...service.meta, canonical: canonicalFor(`/services/${type}`) });
    }
  }, [type, service]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      addBreadcrumbSchema(breadcrumbsFor(`/services/${type}`));
    }
  }, [type, service]);

  if (!service) {
    return (
      <div style={{ paddingTop: '140px', minHeight: '60vh', textAlign: 'center', padding: '140px 1.5rem 2rem' }}>
        <h1>Service Not Found</h1>
        <p style={{color:'#6b7280',marginBottom:'1rem'}}>We couldn't find that service.</p>
        <Link to="/services" style={{ color: '#2563eb' }}>← Back to Services</Link>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '140px', minHeight: '60vh' }}>
      <section style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: 'white', padding: '60px 0', textAlign: 'center' }}>
        <div className="container">
          <h1 style={{ fontSize: '2.5rem', fontFamily: "'Playfair Display', serif", marginBottom: '0.5rem' }}>{service.title}</h1>
          <p style={{ fontSize: '1.2rem', opacity: 0.85 }}>{service.tagline}</p>
        </div>
      </section>
      <section style={{ padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1.5rem' }}>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#374151', marginBottom: '2rem' }}>{service.description}</p>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', fontFamily: "'Playfair Display', serif" }}>What's Included</h2>
          <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
            {features.map((f, i) => (
              <li key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '10px', color: '#374151' }}>
                <span style={{ color: '#059669', fontWeight: 'bold' }}>✓</span>{f}
              </li>
            ))}
          </ul>
          <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '24px', marginBottom: '2rem' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1rem', color: '#6b7280' }}>Ideal For</h3>
            <p style={{ margin: '0 0 16px', color: '#374151' }}>{service.idealFor}</p>
            {displayPrice && (
              <>
                <h3 style={{ margin: '0 0 8px', fontSize: '1rem', color: '#6b7280' }}>
                  {service.quoteOnly ? 'Pricing' : 'Starting At'}
                </h3>
                <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1a1a2e' }}>{displayPrice}</p>
              </>
            )}
            {service.priceNote && (
              <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: '#6b7280' }}>{service.priceNote}</p>
            )}
            {!service.quoteOnly && (
              <p style={{ margin: '10px 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                Booking several this year?{' '}
                <Link to="/credit" style={{ color: '#2563eb', fontWeight: 600 }}>Prepaid credit</Link>{' '}
                lowers this.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {/* Quote-only services have no bookable package behind them, so
                sending someone into the booking flow would dead-end. */}
            <Link
              to={service.quoteOnly ? '/contact' : (service.bookLink || '/book')}
              style={{ padding: '14px 32px', background: '#c9a96e', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: '600' }}
            >
              {service.quoteOnly ? 'Request a Quote' : 'Book This Service'}
            </Link>
            <Link to="/services" style={{ padding: '14px 32px', border: '2px solid #1a1a2e', color: '#1a1a2e', borderRadius: '6px', textDecoration: 'none', fontWeight: '500' }}>View All Packages</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Main services page
function ServicesPage() {
  const [expandedAddOn, setExpandedAddOn] = useState(null);

  // Adapt a central-config package to the Services.jsx display shape.
  // Central shape: { _id, name, price, description, features, popular, active }
  //   features may include a trailing note like "*Properties up to 2,000 sq ft"
  // Services.jsx shape: { id, name, price, description, features, note, best }
  //   where name is shortened (drops " Package" suffix) and note is pulled out of features.
  const adaptPackageForDisplay = (pkg) => {
    const allFeatures = Array.isArray(pkg.features) ? pkg.features : [];
    const noteFeature = allFeatures.find(f => typeof f === 'string' && f.startsWith('*'));
    const features = allFeatures.filter(f => f !== noteFeature);
    return {
      id: pkg._id || pkg.id,
      name: (pkg.name || '').replace(/\s*Package$/, ''),
      price: pkg.price,
      description: pkg.description,
      features,
      note: noteFeature || null,
      best: !!pkg.popular,
      land: pkg.category === 'land'
    };
  };

  const [packages, setPackages] = useState(() =>
    getDefaultPackages().map(adaptPackageForDisplay)
  );
  const [addOns, setAddOns] = useState(() => getDefaultAddOns());
  // Land is priced by size (2026-10-12); examples are computed, never typed.
  const landCfg = getLandAcreageConfig();
  const money = (n) => `$${Number(n).toLocaleString('en-US')}`;
  const landBase = (packages.find(pkg => pkg.land) || {}).price || 0;
  const landPrice = (acres) => landBase + landExtraAcres(acres, landCfg) * landCfg.pricePerAcre;

  useEffect(() => {
    updatePageMeta(metaFor('/services'));

    // Refresh from server
    let cancelled = false;
    (async () => {
      try {
        const pricing = await getPricing();
        if (cancelled) return;
        if (Array.isArray(pricing?.packages) && pricing.packages.length > 0) {
          setPackages(pricing.packages.map(adaptPackageForDisplay));
        }
        if (Array.isArray(pricing?.addOns) && pricing.addOns.length > 0) {
          setAddOns(pricing.addOns);
        }
      } catch {
        // Already have fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ paddingTop: '140px', minHeight: '100vh', background: '#f9fafb' }}>
      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '40px 1.5rem 20px' }}>
        <h1 style={{ fontSize: '2.5rem', fontFamily: "'Playfair Display', serif", color: '#1a1a2e', marginBottom: '0.5rem' }}>Photography Packages</h1>
        <p style={{ fontSize: '1.1rem', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
          Professional real estate photography for every listing. One-time booking, no commitment.
        </p>
      </section>

      {/* Package Cards */}
      <section style={{ padding: '20px 0 50px' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
            {packages.filter(pkg => !pkg.land).map(pkg => (
              <div key={pkg.id} style={{
                background: 'white', borderRadius: '12px', overflow: 'hidden',
                boxShadow: pkg.best ? '0 8px 30px rgba(37,99,235,0.15)' : '0 2px 10px rgba(0,0,0,0.06)',
                border: pkg.best ? '2px solid #2563eb' : '1px solid #e5e7eb',
                position: 'relative', display: 'flex', flexDirection: 'column'
              }}>
                {pkg.best && <div style={{ background: '#2563eb', color: 'white', textAlign: 'center', padding: '6px', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.05em' }}>MOST POPULAR</div>}
                <div style={{ padding: '24px 24px 0' }}>
                  <h3 style={{ fontSize: '1.25rem', margin: '0 0 4px' }}>{pkg.name}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0 0 12px' }}>{pkg.description}</p>
                  <div style={{ fontSize: '2.25rem', fontWeight: '700', color: '#1a1a2e' }}>
                    ${pkg.price}<span style={{ fontSize: '1rem', fontWeight: '400', color: '#9ca3af' }}>/session</span>
                  </div>
                </div>
                <div style={{ padding: '16px 24px', flex: 1 }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {pkg.features.map((f, i) => (
                      <li key={i} style={{ padding: '5px 0', fontSize: '0.9rem', color: '#374151', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <span style={{ color: '#059669', flexShrink: 0 }}>✓</span>{f}
                      </li>
                    ))}
                  </ul>
                  {pkg.note && (
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic', margin: '10px 0 0', padding: '0' }}>{pkg.note}</p>
                  )}
                </div>
                <div style={{ padding: '16px 24px 24px' }}>
                  <Link to="/book" style={{
                    display: 'block', textAlign: 'center', padding: '12px',
                    background: pkg.best ? '#2563eb' : '#1a1a2e', color: 'white',
                    borderRadius: '8px', textDecoration: 'none', fontWeight: '600', fontSize: '0.95rem'
                  }}>Book Now</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Land & lots (2026-10-11) */}
      {packages.some(pkg => pkg.land) && (
        <section id="land" style={{ padding: '10px 0 50px' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
              <h2 style={{ fontSize: '1.75rem', fontFamily: "'Playfair Display', serif", color: '#1a1a2e', marginBottom: '0.5rem' }}>Land & Lots</h2>
              <p style={{ color: '#6b7280', maxWidth: '620px', margin: '0 auto' }}>
                Aerial photos covering the whole parcel with the boundary drawn on, plus ground photos. One price by size, any size, booked online. <Link to="/services/land" style={{ color: '#2563eb' }}>How land shoots work</Link>
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
              {packages.filter(pkg => pkg.land).map(pkg => (
                <div key={pkg.id} style={{
                  background: 'white', borderRadius: '12px', overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb',
                  display: 'flex', flexDirection: 'column'
                }}>
                  <div style={{ padding: '24px 24px 0' }}>
                    <h3 style={{ fontSize: '1.25rem', margin: '0 0 4px' }}>{pkg.name}</h3>
                    <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0 0 12px' }}>{pkg.description}</p>
                    <div style={{ fontSize: '2.25rem', fontWeight: '700', color: '#1a1a2e' }}>
                      ${pkg.price}<span style={{ fontSize: '1rem', fontWeight: '400', color: '#9ca3af' }}>/session</span>
                    </div>
                  </div>
                  <div style={{ padding: '16px 24px', flex: 1 }}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {pkg.features.map((f, i) => (
                        <li key={i} style={{ padding: '5px 0', fontSize: '0.9rem', color: '#374151', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                          <span style={{ color: '#059669', flexShrink: 0 }}>✓</span>{f}
                        </li>
                      ))}
                    </ul>
                    {pkg.note && (
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic', margin: '10px 0 0', padding: '0' }}>{pkg.note.replace(/^\*/, '')}</p>
                    )}
                  </div>
                  <div style={{ padding: '16px 24px 24px' }}>
                    <Link to={`/book?package=${encodeURIComponent(pkg.id)}`} style={{
                      display: 'block', textAlign: 'center', padding: '12px',
                      background: '#1a1a2e', color: 'white',
                      borderRadius: '8px', textDecoration: 'none', fontWeight: '600', fontSize: '0.95rem'
                    }}>Book Now</Link>
                  </div>
                </div>
              ))}
              <div style={{
                background: '#f9fafb', borderRadius: '12px', border: '1px dashed #d1d5db',
                padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center'
              }}>
                <h3 style={{ fontSize: '1.25rem', margin: '0 0 6px' }}>Priced by size</h3>
                <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0 0 12px' }}>
                  The first acre is included. Each additional acre, or part of one, adds {money(landCfg.pricePerAcre)} and at least {landCfg.aerialPhotosPerAcre} more aerial photos. Any size books online.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[0.5, 2, 5, 10].map(a => (
                    <li key={a} style={{ padding: '4px 0', fontSize: '0.9rem', color: '#374151', fontWeight: 600 }}>
                      {a === 0.5 ? '½' : a} acre{a > 1 ? 's' : ''} — {money(landPrice(a))}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* À La Carte Add-Ons */}
      <section style={{ padding: '50px 0', background: 'white' }}>
        <div className="container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontFamily: "'Playfair Display', serif", color: '#1a1a2e', marginBottom: '0.5rem' }}>À La Carte Add-Ons</h2>
            <p style={{ color: '#6b7280', maxWidth: '500px', margin: '0 auto' }}>
              Customize any package with individual services. Mix and match to fit your listing's needs.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
            {addOns.map(addon => (
              <div key={addon.id} style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '16px 18px', background: '#f9fafb', borderRadius: '10px',
                border: '1px solid #e5e7eb', cursor: 'pointer', transition: 'all 0.2s'
              }}
              onClick={() => setExpandedAddOn(expandedAddOn === addon.id ? null : addon.id)}
              >
                <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{addon.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                    <span style={{ fontWeight: '600', color: '#1a1a2e', fontSize: '0.95rem' }}>{addon.name}</span>
                    <span style={{ fontWeight: '700', color: '#2563eb', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                      ${addon.price}{ADDON_UNIT_SUFFIX[addon.id] || ''}
                    </span>
                  </div>
                  {expandedAddOn === addon.id && (
                    <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#6b7280', lineHeight: '1.4' }}>{addon.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: '1.5rem', color: '#9ca3af', fontSize: '0.85rem' }}>
            Click any add-on for details. Add-ons can be selected during booking.
          </p>
        </div>
      </section>

      {/* Service Types */}
      <section style={{ padding: '50px 0', background: '#f9fafb' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', fontFamily: "'Playfair Display', serif", marginBottom: '2rem' }}>Explore Our Services</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {Object.entries(serviceDetail).map(([key, svc]) => (
              <Link key={key} to={`/services/${key}`} style={{
                display: 'block', padding: '24px', background: 'white', borderRadius: '10px',
                textDecoration: 'none', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s, transform 0.2s'
              }}>
                <h3 style={{ fontSize: '1.1rem', color: '#1a1a2e', marginBottom: '6px' }}>{svc.title}</h3>
                <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0 0 8px', lineHeight: '1.5' }}>{svc.tagline}</p>
                <span style={{ color: '#2563eb', fontSize: '0.85rem', fontWeight: '600' }}>Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA. Replaced a monthly-subscription pitch for a product
          retired from public view (code intact — HANDOFF standing reminders). */}
      <section style={{ padding: '50px 1.5rem', textAlign: 'center', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: 'white' }}>
        <h2 style={{ fontSize: '1.75rem', fontFamily: "'Playfair Display', serif", marginBottom: '0.75rem' }}>Every Price, Published</h2>
        <p style={{ fontSize: '1.05rem', opacity: 0.85, maxWidth: '550px', margin: '0 auto 1.5rem' }}>
          Fixed package prices and add-ons, plus prepaid credit packs that add bonus credit for agents who list often.
        </p>
        <Link to="/pricing" style={{
          display: 'inline-block', padding: '14px 36px', background: '#c9a96e', color: 'white',
          borderRadius: '8px', textDecoration: 'none', fontWeight: '600', fontSize: '1rem'
        }}>See Pricing</Link>
      </section>
    </div>
  );
}

function Services() {
  const { type } = useParams();
  if (type) return <ServiceDetail type={type} />;
  return <ServicesPage />;
}

export default Services;
